import { Injectable } from '@angular/core';
import { Type } from '@angular/core';
import { EmptyNodeEditorComponent } from './component/details-editor/transport/empty-node-editor/empty-node-editor.component';
import { CarrierConditionEditorComponent } from './component/details-editor/transport/carrier-condition-editor/carrier-condition-editor.component';
import { CurrentPositionEditorComponent } from './component/details-editor/transport/current-position-editor/current-position-editor.component';
import { CurrentStationLocationEditorComponent } from './component/details-editor/transport/current-station-location-editor/current-station-location-editor.component';
import { CurrentStationTypeEditorComponent } from './component/details-editor/transport/current-station-type-editor/current-station-type-editor.component';
import { NextProcessingPositionEditorComponent } from './component/details-editor/transport/next-processing-position-editor/next-processing-position-editor.component';
import { PositionInRouteEditorComponent } from './component/details-editor/transport/position-in-route-editor/position-in-route-editor.component';
import { String1ConditionEditorComponent } from './component/details-editor/transport/string1-condition-editor/string1-condition-editor.component';
import { String2ConditionEditorComponent } from './component/details-editor/transport/string2-condition-editor/string2-condition-editor.component';
import { TransportOptionConditionEditorComponent } from './component/details-editor/transport/transport-option-condition-editor/transport-option-condition-editor.component';
import { ProcessingStatusEditorComponent } from './component/details-editor/transport/processing-status-editor/processing-status-editor.component';
import { ConditionGroup, ConditionType } from './filter-administration.model';
import { OpenTaksOfTypeEditorComponent } from './component/details-editor/transport/open-task-editor/open-task-editor.component';
import { TransportStatusEditorComponent } from './component/details-editor/transport/transport-status-editor/transport-status-editor.component';
import { TransportTypeEditorComponent } from './component/details-editor/transport/transport-type-editor/transport-type-editor.component';
import { OrderReferenceEditorComponent } from './component/details-editor/transport/order-reference-editor/order-reference-editor.component';
import { TransportScopeEditorComponent } from './component/details-editor/transport/transport-scope/transport-scope-editor.component';
import { TransportType } from 'src/tmc-yard/components/transport-details/transport-details-model';
import { VisitorRoleConditionEditorComponent } from './component/details-editor/visitor/visitor-role-condition-editor/visitor-role-condition-editor.component';
import { VisitorVisitEndConditionEditorComponent } from './component/details-editor/visitor/visitor-visit-end-condition-editor/visitor-visit-end-condition-editor.component';
import { HasSupplementaryEquipmentConditionEditorComponent } from './component/details-editor/common/visitor-has-supplementary-equipment-condition-editor/has-supplementary-equipment-condition-editor.component';
import { CargoPositionConditionEditorComponent } from './component/details-editor/transport/cargo-position-condition-editor/cargo-position-condition-editor.component';
import { WeekdayEditorComponent } from './component/details-editor/transport/weekday-editor/weekday-editor.component';
import { HoursConditionEditorComponent } from './component/details-editor/transport/hours-condition-editor/hours-condition-editor.component';

export class FilterEditor {
  key: string;
  editorComponent: Type<any>;

  constructor(key: string, editorComponent: Type<any>) {
    this.key = key;
    this.editorComponent = editorComponent;
  }
}

@Injectable({
  providedIn: 'root'
})
export class FilterEditorRegistryService {
  transportConditionGroups = new Array<ConditionGroup>();
  visitorConditionGroups = new Array<ConditionGroup>();
  editorsByKey = new Map<string, FilterEditor>();
  conditionTypesByKey = new Map<string, ConditionType>();

  constructor() {
    this.registerTransportConditions();
    this.registerVisitorConditions();
  }

  private registerTransportConditions() {
    this.transportConditionGroups.push({
      nameDictionaryKey: 'tmc-yard.widgets.filter-administration.condition-group.transport-properties', types: [
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.carrier', type: 'carrier' },
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.string-1', type: 'string1' },
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.string-2', type: 'string2' },
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.transport-option', type: 'transportOption' },
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.important', type: 'important' },
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.transport-status', type: 'transportStatus' },
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.transport-type', type: 'transportType', defaultData: { transportType: TransportType.TRANSPORT } },
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.order-reference', type: 'orderReference' },
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.transport-scope', type: 'transportScope' },
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.cargo-in-positions', type: 'cargoInPositions' },
        {
          nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.visitor-status.has-supplementary-equipment-of-type', type: 'hasSupplementaryEquipmentOfType'
        }
      ]
    });

    this.transportConditionGroups.push({
      nameDictionaryKey: 'tmc-yard.widgets.filter-administration.condition-group.location', types: [
        {
          nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.current-station-type',
          type: 'currentStationType', defaultData: { stationType: 'WORKING_AREA' }
        },
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.current-station-location', type: 'currentStationLocation', defaultData: { location: 'INTERNAL' } }
      ]
    });

    this.transportConditionGroups.push({
      nameDictionaryKey: 'tmc-yard.widgets.filter-administration.condition-group.control', types: [
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.withoutRouting', type: 'withoutRouting' },
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.current-position', type: 'currentPosition' },
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.next-processing-position', type: 'nextProcessingPosition' },
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.position-in-route', type: 'positionInRoute' },
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.processing-status', type: 'processingStatus' },
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.waiting-for-pulling', type: 'waitingForPulling' },
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.finished', type: 'finished' },
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.planned', type: 'planned' },
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.intermitted', type: 'intermitted' },
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.is-time', type: 'isTime' },
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.planned-in-next-24-hours', type: 'plannedInNext24Hours' },
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.planned-in-next-x-hours', type: 'plannedInNextXHours' },
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.planned-tomorrow', type: 'plannedTomorrow' },
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.in-processing-or-planned-for-today', type: 'inProcessingOrPlannedForToday' },
      ]
    });

    this.transportConditionGroups.push({
      nameDictionaryKey: 'tmc-yard.widgets.filter-administration.condition-group.task-state', types: [
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.has-no-tasks', type: 'hasNoTasks' },
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.has-any-tasks', type: 'hasAnyTasks' },
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.has-blocking-tasks', type: 'hasBlockingTasks' },
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.has-open-tasks-of-type', type: 'hasOpenTaskOfType' },
      ]
    });

    for (const cg of this.transportConditionGroups) {
      for (const type of cg.types) {
        this.conditionTypesByKey.set(type.type, type);
      }
    }

    this.registerFilterEditor(new FilterEditor('carrier', CarrierConditionEditorComponent));
    this.registerFilterEditor(new FilterEditor('string1', String1ConditionEditorComponent));
    this.registerFilterEditor(new FilterEditor('string2', String2ConditionEditorComponent));
    this.registerFilterEditor(new FilterEditor('important', EmptyNodeEditorComponent));
    this.registerFilterEditor(new FilterEditor('transportOption', TransportOptionConditionEditorComponent));
    this.registerFilterEditor(new FilterEditor('currentStationType', CurrentStationTypeEditorComponent));
    this.registerFilterEditor(new FilterEditor('currentStationLocation', CurrentStationLocationEditorComponent));
    // this.registerFilterEditor(new FilterEditor('withRouting', CurrentPositionEditorComponent));
    this.registerFilterEditor(new FilterEditor('currentPosition', CurrentPositionEditorComponent));
    this.registerFilterEditor(new FilterEditor('nextProcessingPosition', NextProcessingPositionEditorComponent));
    this.registerFilterEditor(new FilterEditor('positionInRoute', PositionInRouteEditorComponent));
    this.registerFilterEditor(new FilterEditor('processingStatus', ProcessingStatusEditorComponent));
    this.registerFilterEditor(new FilterEditor('waitingForPulling', EmptyNodeEditorComponent));
    this.registerFilterEditor(new FilterEditor('finished', EmptyNodeEditorComponent));
    this.registerFilterEditor(new FilterEditor('planned', EmptyNodeEditorComponent));
    this.registerFilterEditor(new FilterEditor('intermitted', EmptyNodeEditorComponent));
    this.registerFilterEditor(new FilterEditor('plannedInNext24Hours', EmptyNodeEditorComponent));
    this.registerFilterEditor(new FilterEditor('plannedInNextXHours', HoursConditionEditorComponent));
    this.registerFilterEditor(new FilterEditor('cargoInPositions', CargoPositionConditionEditorComponent));
    this.registerFilterEditor(new FilterEditor('inProcessingOrPlannedForToday', EmptyNodeEditorComponent));
    this.registerFilterEditor(new FilterEditor('isTime', EmptyNodeEditorComponent));
    this.registerFilterEditor(new FilterEditor('hasNoTasks', EmptyNodeEditorComponent));
    this.registerFilterEditor(new FilterEditor('hasTasks', EmptyNodeEditorComponent));
    this.registerFilterEditor(new FilterEditor('hasBlockingTasks', EmptyNodeEditorComponent));
    this.registerFilterEditor(new FilterEditor('hasOpenTaskOfType', OpenTaksOfTypeEditorComponent));
    this.registerFilterEditor(new FilterEditor('transportStatus', TransportStatusEditorComponent));
    this.registerFilterEditor(new FilterEditor('transportType', TransportTypeEditorComponent));
    this.registerFilterEditor(new FilterEditor('orderReference', OrderReferenceEditorComponent));
    this.registerFilterEditor(new FilterEditor('transportScope', TransportScopeEditorComponent));
    this.registerFilterEditor(new FilterEditor('plannedTomorrow', WeekdayEditorComponent));
  }

  private registerVisitorConditions() {
    this.visitorConditionGroups.push({
      nameDictionaryKey: 'tmc-yard.widgets.filter-administration.condition-group.visitor-type', types: [
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.visitor-type.group', type: 'visitorTypeGroup' },
        { nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.visitor-type.visitor-role-filter', type: 'visitorRole' },
      ]
    });
    this.visitorConditionGroups.push({
      nameDictionaryKey: 'tmc-yard.widgets.filter-administration.condition-group.visitor-status', types: [
        {
          nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.visitor-status.expected', type: 'visitorStatusExpected'
        },
        {
          nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.visitor-status.registered', type: 'visitorStatusRegistered'
        },
        {
          nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.visitor-status.visit-end', type: 'visitorStatusVisitEnd'
        },
        {
          nameDictionaryKey: 'tmc-yard.widgets.filter-administration.filter-conditions.visitor-status.has-supplementary-equipment-of-type', type: 'hasSupplementaryEquipmentOfType'
        }
      ]
    });

    for (const cg of this.visitorConditionGroups) {
      for (const type of cg.types) {
        this.conditionTypesByKey.set(type.type, type);
      }
    }

    this.registerFilterEditor(new FilterEditor('visitorTypeGroup', EmptyNodeEditorComponent));
    this.registerFilterEditor(new FilterEditor('visitorStatusExpected', EmptyNodeEditorComponent));
    this.registerFilterEditor(new FilterEditor('visitorStatusRegistered', EmptyNodeEditorComponent));
    this.registerFilterEditor(new FilterEditor('visitorRole', VisitorRoleConditionEditorComponent));
    this.registerFilterEditor(new FilterEditor('visitorStatusVisitEnd', VisitorVisitEndConditionEditorComponent));
    this.registerFilterEditor(new FilterEditor('hasSupplementaryEquipmentOfType', HasSupplementaryEquipmentConditionEditorComponent));

  }

  public getConditionType(type: string): ConditionType {
    return this.conditionTypesByKey.get(type);
  }

  public getTransportConditionGroups(): Array<ConditionGroup> {
    return this.transportConditionGroups;
  }

  public getVisitorConditionGroups() {
    return this.visitorConditionGroups;
  }

  public registerFilterEditor(editor: FilterEditor) {
    this.editorsByKey.set(editor.key, editor);
  }

  public getEditor(key: string): FilterEditor {
    return this.editorsByKey.get(key);
  }

}
